<template>
	<div class="tabBox_wrap sticky_tab">
		<div ref="tabBox" class="tab_box">
			<button
				v-for="(item, index) in props.items"
				:key="index"
				:ref="(el) => setItemRef(el, index)"
				:value="item"
				class="tab_item tab_scroll"
				:class="item === selectedTab && 'selected'"
				@click="onClickTab(item)"
			>
				<img
					v-if="
						(headerStore.getCountry === 'USA' || headerStore.getCountry === 'JPN') &&
						!props.auth &&
						limitedCategory_USA_JPN.includes(item)
					"
					src="@/assets/img/report/lock.svg"
					alt="lock"
					loading="lazy"
				/>
				<p>{{ item }}</p>
			</button>
		</div>
	</div>
</template>

<script lang="ts" setup>
// core
import { ComponentPublicInstance, computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
// store
import { useHeaderStore } from '@/stores/header';
import { useModalStore } from '@/stores/modal';

// -----variable-----
const props = defineProps<{
	items: string[];
	tabPk?: number;
	auth?: boolean;
}>();

const emit = defineEmits(['update:category']);
const route = useRoute();
const router = useRouter();
const headerStore = useHeaderStore();
const modalStore = useModalStore();
const limitedCategory_USA_JPN = ['점수', '주요사업'];
const tabBox = ref<HTMLDivElement | null>(null);
const refsArray = ref<HTMLElement[]>([]);
const selectedTab = computed(() => {
	return route.query[props.tabPk ? `tab${props.tabPk}` : 'tab'] ?? props.items[0];
});

// -----function-----
// HTML 요소를 중앙에 위치시키는 함수
const scrollToCenter = (element: HTMLElement) => {
	if (tabBox.value) {
		const container = tabBox.value;
		const containerWidth = container.clientWidth;
		const elementWidth = element.clientWidth;
		const elementLeft = element.offsetLeft;
		const scrollPosition = elementLeft - containerWidth / 2 + elementWidth / 2;
		container.scrollTo({
			left: scrollPosition,
			behavior: 'smooth',
		});
	}
};

// 탭 클릭 시 실행 함수
const onClickTab = (tab: string) => {
	if (
		(headerStore.getCountry === 'USA' || headerStore.getCountry === 'JPN') &&
		!props.auth &&
		limitedCategory_USA_JPN.includes(tab)
	) {
		modalStore.openModal('GuideModal');
		return;
	}

	if (props.tabPk) {
		if (route.query[`sort${props.tabPk}`]) {
			delete route.query[`sort${props.tabPk}`];
		}
		router.replace({ query: { ...route.query, [`tab${props.tabPk}`]: tab } });
	} else {
		router.replace({ query: { tab: tab } });
	}

	emit('update:category', tab);
};

// 동적으로 Ref 부여하는 함수
const setItemRef = (el: Element | ComponentPublicInstance | null, index: number) => {
	if (el instanceof HTMLButtonElement) refsArray.value[index] = el;
};

// -----composition-----
onMounted(async () => {
	const initialTab = route.query.tab || props.items?.[0];
	const initialTabElement = tabBox.value?.querySelector<HTMLElement>(`.tab_item[value="${initialTab}"]`);
	if (initialTabElement) {
		scrollToCenter(initialTabElement);
	}
});

watch(
	() => selectedTab.value,
	(newTab) => {
		const newTabElement = tabBox.value?.querySelector<HTMLElement>(`.tab_item[value="${newTab}"]`);
		if (newTabElement) {
			scrollToCenter(newTabElement);
		}
	}
);
</script>

<style lang="scss" scoped>
.tabBox_wrap {
	display: flex;
	justify-content: space-between;
	gap: 1rem;
	border-bottom: 1px solid #393d45;
	position: sticky;
	/* top: calc(var(--header-height) - 0.2rem); // 여백이 보이는 경우가 있어서 -2px 추가 */
	top: -0.1rem;
	background-color: var(--color-bg);
	z-index: 2;
}

.tab_box {
	display: flex;
	flex-direction: row;
	max-width: 68rem;
	overflow: scroll;
	width: 100%;
	.tab_item {
		display: flex;
		justify-content: center;
		align-items: center;
		gap: 0.8rem;
		width: 100%;
		height: 5.2rem;
		white-space: nowrap;
		@media screen and (max-width: 1280px) {
			padding: 1.6rem 2rem;
		}
		img {
			width: 2.4rem;
			height: 2.4rem;
		}
		p {
			font-size: var(--font-s-regular);
			font-weight: var(--font-w-medium);
			line-height: 1.7;
		}
	}
	.selected {
		border-bottom: 1px solid var(--color-primary);
		color: var(--color-primary);
	}
}

.tab_box::-webkit-scrollbar {
	display: none;
}

.tab_allItem {
	display: flex;
	flex-direction: column;
	gap: 1.6rem;
	position: absolute;
	top: 103%;
	right: 0;
	z-index: 89;
	background-color: var(--color-bg-mid);
	padding: 2.4rem;
	border-radius: 0.8rem;
	box-shadow: 4px 4px 10px 0px #0000004d;
	@media screen and (max-width: 1280px) {
		margin: 0 2rem;
	}

	.allItem_title {
		font-size: var(--font-s-large);
		font-weight: var(--font-w-semi-bold);
	}
	.allItem_content {
		display: flex;
		flex-wrap: wrap;
		gap: 1rem;
		.content_menu {
			font-size: var(--font-s-mid);
			font-weight: var(--font-w-medium);
			line-height: 1.79rem;
			white-space: nowrap;
			padding: 1.3rem 1.6rem;
			border: 1px solid var(--color-base-mid);
			border-radius: 6px;
		}
	}
}
.dimmed {
	position: fixed;
	background-color: #171616bf;
	top: 0px;
	left: 0px;
	height: 100%;
	width: 100%;
	z-index: 80;
}
</style>
